import Phaser from "phaser";
import { DIFFICULTIES, scenarioById } from "../config/campaign";
import { CASTLE_APPROACH, MAPS } from "../config/map";
import { AudioManager } from "../systems/audio";
import { loadSave } from "../systems/save";
import { addButton, addCastleBackdrop, addLittleCastleFooter, COLORS } from "./shared";

export class ChapterIntroScene extends Phaser.Scene {
  private audio!: AudioManager;

  constructor() {
    super("ChapterIntro");
  }

  create(): void {
    addCastleBackdrop(this);
    const map = MAPS[this.registry.get("selected-map-id") as string] ?? CASTLE_APPROACH;
    const scenario = scenarioById(this.registry.get("selected-scenario-id") as string | undefined);
    this.audio = new AudioManager(loadSave().settings);
    this.audio.bindUnlock(this);
    this.audio.cue("parchment");

    const centerX = this.scale.width / 2;
    const centerY = this.scale.height / 2;
    const width = Math.min(700, this.scale.width - 48);
    const height = Math.min(430, this.scale.height - 110);

    const shadow = this.add.rectangle(centerX + 8, centerY + 10, width, height, 0x000000, 0.45);
    const parchment = this.add
      .rectangle(centerX, centerY, width, height, 0xe8d8ae, 0.98)
      .setStrokeStyle(5, 0x7d5a2e);
    const inner = this.add
      .rectangle(centerX, centerY, width - 24, height - 24, 0xf4e8c8, 0.9)
      .setStrokeStyle(2, COLORS.goldDark, 0.75);
    const top = centerY - height / 2;
    const sealY = top + 52;
    const headingY = top + 112;
    const intelY = top + 154;
    const bodyY = centerY + 30;
    const buttonY = centerY + height / 2 - 52;

    const seal = this.add.circle(centerX, sealY, 28, 0x8e2f34).setStrokeStyle(3, 0xd8b45c);
    const crest = this.add
      .text(centerX, sealY, "♜", {
        fontFamily: "Georgia, serif",
        fontSize: "30px",
        color: "#f5d77b",
      })
      .setOrigin(0.5);

    const heading = this.add
      .text(centerX, headingY, map.displayName, {
        fontFamily: "Georgia, serif",
        fontSize: this.scale.width < 700 ? "30px" : "42px",
        fontStyle: "bold",
        color: "#382619",
        align: "center",
      })
      .setOrigin(0.5);

    const difficulty = scenario?.displayName ?? "Easy";
    const waveCount = DIFFICULTIES[scenario?.difficulty ?? "easy"].waveCount;
    const intel = this.add
      .text(
        centerX,
        intelY,
        `MISSION INTELLIGENCE  •  ${waveCount} WAVES  •  ${scenario?.startingLives ?? 20} LIVES`,
        {
          fontFamily: "Trebuchet MS, sans-serif",
          fontSize: this.scale.width < 700 ? "11px" : "13px",
          fontStyle: "bold",
          color: "#7a512a",
        },
      )
      .setOrigin(0.5);

    const body = this.add
      .text(
        centerX,
        bodyY,
        `My Lord,\n\nScouts report that the enemy is advancing on ${map.displayName}. The ${difficulty.toLowerCase()} campaign begins now. Ready the companies, preserve the road, and let no runner reach the keep.`,
        {
          fontFamily: "Georgia, serif",
          fontSize: this.scale.width < 700 ? "17px" : "21px",
          color: "#493420",
          align: "center",
          lineSpacing: 8,
          wordWrap: { width: width - 90 },
        },
      )
      .setOrigin(0.5);

    const start = addButton(
      this,
      centerX,
      buttonY,
      250,
      "Begin the Defense",
      () => {
        this.audio.cue("battle-start");
        this.cameras.main.fadeOut(280, 0, 0, 0);
        this.time.delayedCall(290, () => this.scene.start("Game"));
      },
      true,
    );

    [shadow, parchment, inner, seal, crest, heading, intel, body, start].forEach((object) =>
      object.setAlpha(0),
    );
    this.tweens.add({
      targets: [shadow, parchment, inner],
      alpha: 1,
      scaleX: { from: 0.92, to: 1 },
      scaleY: { from: 0.92, to: 1 },
      duration: 420,
      ease: "Back.Out",
    });
    this.tweens.add({
      targets: [seal, crest, heading, intel, body, start],
      alpha: 1,
      duration: 420,
      delay: 220,
    });
    addLittleCastleFooter(this, "Royal Campaign Dispatch");
  }
}
